import { CONFIGURED_ABLY_CHANNEL_NAMESPACE } from "_shared/_utilities/meta";
import type Session from "users/__session/session";

export const ablyChannelBases = {
  systemMessages: "system-messages",
  assistantTurns: "ai-assistant:turns",
  documents: "documents",
  entityMemberInvitations: "legal-entities:member-invitations",
  messages: "messages",
  networkConnections: "network:connections",
  networkInvitations: "network:invitations",
  notifications: "notifications",
  entityNotifications: "notifications:entity",
  entityPermissions: "permissions:entity",
  proposals: "work:proposals",
  teamTemplates: "work:team-templates",
  workAgreements: "work:agreements",
} as const;

/**
 * Every channel name passes through here, so one prefix isolates the whole surface.
 *
 * Must match AblyRealtimeChannelNames on the server exactly: the API both publishes to these names
 * and mints Ably tokens whose capabilities are keyed by them, so a prefix on one side only is a
 * channel nobody publishes to and an attach the token refuses. Empty outside the functional suite,
 * which leaves every name as it was.
 */
const prefixed = (channelName: string) =>
  CONFIGURED_ABLY_CHANNEL_NAMESPACE ? `${CONFIGURED_ABLY_CHANNEL_NAMESPACE}:${channelName}` : channelName;

const forUser = (channelBase: string, userId?: string) =>
  userId ? prefixed(`${channelBase}:user:${userId}`) : undefined;
const forEntity = (channelBase: string, legalEntityId?: string) =>
  legalEntityId ? prefixed(`${channelBase}:${legalEntityId}`) : undefined;
const getSessionUserId = (session: Session) => session.user?.id?.value;
const getSessionLegalEntityId = (session: Session) => session.user?.legalEntityId?.value;
/**
 * The entity feed is the one channel a signed-in session is not automatically entitled to, so its
 * legal entity alone does not name it: only a holder of an entity-wide notifications grant does
 * (issue #4438). A session that answers false here has no channel name and therefore never asks.
 */
const getEntityNotificationsLegalEntityId = (session: Session) =>
  session.user?.hasEntityWideNotificationsAccess ? getSessionLegalEntityId(session) : undefined;
/**
 * The organization's permissions channel names whose grants changed, so only somebody who may read
 * every grant in it is entitled to it (issue #6785): a holder of Member Permissions.
 */
const getEntityPermissionsLegalEntityId = (session: Session) =>
  session.user?.hasMemberPermissionsAccess ? getSessionLegalEntityId(session) : undefined;

const ablyChannelNames = {
  assistantTurns: (userId?: string) => forUser(ablyChannelBases.assistantTurns, userId),
  documents: (userId?: string) => forUser(ablyChannelBases.documents, userId),
  entityMemberInvitations: (userId?: string) => forUser(ablyChannelBases.entityMemberInvitations, userId),
  messages: (userId?: string) => forUser(ablyChannelBases.messages, userId),
  networkConnections: (userId?: string) => forUser(ablyChannelBases.networkConnections, userId),
  networkInvitations: (userId?: string) => forUser(ablyChannelBases.networkInvitations, userId),
  notifications: (userId?: string) => forUser(ablyChannelBases.notifications, userId),
  entityNotifications: (legalEntityId?: string) => forEntity(ablyChannelBases.entityNotifications, legalEntityId),
  entityPermissions: (legalEntityId?: string) => forEntity(ablyChannelBases.entityPermissions, legalEntityId),
  proposals: (userId?: string) => forUser(ablyChannelBases.proposals, userId),
  teamTemplates: (userId?: string) => forUser(ablyChannelBases.teamTemplates, userId),
  workAgreements: (userId?: string) => forUser(ablyChannelBases.workAgreements, userId),
} as const;

export const ablyChannelNamesForSession = {
  systemMessages: (session: Session) => (session.user?.id ? prefixed(ablyChannelBases.systemMessages) : undefined),
  // What the user's own assistant turns are doing while they run (#7286).
  assistantTurns: (session: Session) => ablyChannelNames.assistantTurns(getSessionUserId(session)),
  documents: (session: Session) => ablyChannelNames.documents(getSessionUserId(session)),
  entityMemberInvitations: (session: Session) => ablyChannelNames.entityMemberInvitations(getSessionUserId(session)),
  messages: (session: Session) => ablyChannelNames.messages(getSessionUserId(session)),
  networkConnections: (session: Session) => ablyChannelNames.networkConnections(getSessionUserId(session)),
  networkInvitations: (session: Session) => ablyChannelNames.networkInvitations(getSessionUserId(session)),
  notifications: (session: Session) => ablyChannelNames.notifications(getSessionUserId(session)),
  // Whether a session may actually attach here is still decided by the Ably token the API mints:
  // the channel is only in a user's capabilities while they hold an entity-wide notifications
  // grant (issue #4438). This mirrors that decision from the same grant, reported on the login
  // response, so a session without the grant does not attach and get refused with a 40160 on every
  // page load; the server, not this, is what stops a session that asks anyway. Naming the channel
  // from the session's own entity means a client can never even ask for somebody else's feed.
  entityNotifications: (session: Session) =>
    ablyChannelNames.entityNotifications(getEntityNotificationsLegalEntityId(session)),
  // The same arrangement for the organization's permissions channel: the API's token carries it
  // only for a holder of Member Permissions, and the login response says whether this session is one.
  entityPermissions: (session: Session) =>
    ablyChannelNames.entityPermissions(getEntityPermissionsLegalEntityId(session)),
  proposals: (session: Session) => ablyChannelNames.proposals(getSessionUserId(session)),
  teamTemplates: (session: Session) => ablyChannelNames.teamTemplates(getSessionUserId(session)),
  workAgreements: (session: Session) => ablyChannelNames.workAgreements(getSessionUserId(session)),
} as const;

export default ablyChannelNames;
